import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { exportUrl, getStats } from "../../api/admin";
import type { Stats, StatsPeriod } from "../../api/admin";
import { buttonClass, Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { StatusGlyph } from "../../components/StatusGlyph";
import { STATUSES } from "../../domain/status";
import { nl } from "../../i18n/nl";
import { formatDate } from "../../lib/format";
import styles from "./stats.module.css";

const t = nl.stats;
const PERIODS: readonly StatsPeriod[] = ["month", "semester", "all"];
const REASONS = ["it_extern", "geen_stages", "geen_begeleider", "al_volzet", "anders"] as const;

type SortKey = keyof typeof t.columns;

/** Largest-remainder rounding, so the percentages add up to 100. */
export function percentages(values: number[]): number[] {
  const total = values.reduce((a, b) => a + b, 0);
  if (total === 0) return values.map(() => 0);
  const raw = values.map((v) => (v * 100) / total);
  const floors = raw.map(Math.floor);
  let rest = 100 - floors.reduce((a, b) => a + b, 0);
  raw
    .map((r, i) => ({ i, f: r - Math.floor(r) }))
    .sort((a, b) => b.f - a.f)
    .forEach(({ i }) => {
      if (rest > 0) {
        floors[i] = (floors[i] ?? 0) + 1;
        rest -= 1;
      }
    });
  return floors;
}

function dayLabel(day: string): string {
  return formatDate(`${day}T12:00:00Z`);
}

/** "31 aug": short enough for the week axis. */
function shortDay(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("nl-BE", { day: "numeric", month: "short", timeZone: "Europe/Brussels" });
}

function Kpis({ stats }: { stats: Stats }) {
  const lower = t.lower[stats.period];
  const placedShare = stats.students_total ? (stats.students_placed / stats.students_total) * 100 : 0;
  return (
    <dl className={styles.kpis}>
      <div className={styles.kpi}>
        <dt>{t.kpis.companies}</dt>
        <dd className={styles.kpiValue}>{stats.companies_total}</dd>
        <dd className={styles.kpiDelta}>{t.kpis.companiesDelta(stats.companies_new, lower)}</dd>
      </div>
      <div className={styles.kpi}>
        <dt>{t.kpis.reports}</dt>
        <dd className={styles.kpiValue}>{stats.reports}</dd>
        <dd className={styles.kpiDelta}>{lower}</dd>
      </div>
      <div className={styles.kpi}>
        <dt>{t.kpis.nee}</dt>
        <dd className={styles.kpiValue}>{stats.nee_reports}</dd>
        <dd className={styles.kpiDelta}>{t.kpis.neeInfo}</dd>
      </div>
      <div className={styles.kpi}>
        <dt>{t.kpis.placed}</dt>
        <dd className={styles.kpiValue}>
          {stats.students_placed} <span className={styles.kpiSuffix}>{t.kpis.placedOf(stats.students_total)}</span>
        </dd>
        <dd className={styles.meter} aria-hidden="true">
          <span style={{ width: `${placedShare.toFixed(1)}%` }} />
        </dd>
      </div>
    </dl>
  );
}

function StatusBlock({ stats }: { stats: Stats }) {
  const counts = STATUSES.map((s) => stats.status[s] ?? 0);
  const pcts = percentages(counts);
  const total = counts.reduce((a, b) => a + b, 0);
  const parts = STATUSES.map((s, i) => `${nl.status[s]} ${counts[i] ?? 0} (${pcts[i] ?? 0}%)`).join(", ");
  return (
    <section className={styles.card} aria-labelledby="stats-status">
      <h2 id="stats-status" className={styles.cardTitle}>
        {t.statusTitle}
      </h2>
      <p className={styles.sub}>
        {total} · {t.statusSub[stats.period]}
      </p>
      {total > 0 && (
        <div className={styles.stack} role="img" aria-label={t.statusAria(parts)}>
          {STATUSES.map((s, i) =>
            counts[i] ? (
              <span
                key={s}
                className={styles.segment}
                style={{ flexGrow: counts[i], background: `var(--${s}-g)` }}
                title={`${nl.status[s]}: ${counts[i] ?? 0} (${pcts[i] ?? 0}%)`}
              />
            ) : null,
          )}
        </div>
      )}
      <ul className={styles.legend}>
        {STATUSES.map((s, i) => (
          <li key={s}>
            <StatusGlyph status={s} size={14} />
            <span>{nl.status[s]}</span>
            <span className={styles.legendCount}>{counts[i] ?? 0}</span>
            <span className={styles.muted}>{pcts[i] ?? 0}%</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function WeekChart({ stats }: { stats: Stats }) {
  const max = Math.max(1, ...stats.weeks.map((w) => w.count));
  const inPeriod = stats.weeks.filter((w) => w.in_period);
  const peak = Math.max(0, ...inPeriod.map((w) => w.count));
  // Only the first week with the peak gets the number.
  const peakStart = peak > 0 ? inPeriod.find((w) => w.count === peak)?.start : undefined;
  // Label every n-th week so the axis stays readable over a whole year.
  const every = Math.ceil(stats.weeks.length / 8);
  return (
    <section className={styles.card} aria-labelledby="stats-weeks">
      <h2 id="stats-weeks" className={styles.cardTitle}>
        {t.weeksTitle}
      </h2>
      <p className={styles.sub}>{t.weeksSub}</p>
      <ol className={styles.weeks}>
        {stats.weeks.map((week, i) => {
          const label = dayLabel(week.start);
          return (
            <li key={week.start} className={styles.week} aria-label={t.weekAria(label, week.count, week.in_period)}>
              <span className={styles.barArea} aria-hidden="true">
                {week.start === peakStart && (
                  <span className={styles.peak}>{week.count}</span>
                )}
                <span
                  className={[styles.bar, week.in_period ? styles.inPeriod : styles.outPeriod].join(" ")}
                  style={{ height: `${(week.count / max) * 100}%` }}
                />
                <span className={styles.tooltip}>
                  {label}: {t.weekTip(week.count)}
                </span>
              </span>
              <span className={styles.weekLabel} aria-hidden="true">
                {i % every === 0 ? shortDay(week.start) : ""}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Reasons({ stats }: { stats: Stats }) {
  const counts = REASONS.map((r) => stats.reasons[r] ?? 0);
  const pcts = percentages(counts);
  const max = Math.max(1, ...counts);
  const total = counts.reduce((a, b) => a + b, 0);
  return (
    <section className={styles.card} aria-labelledby="stats-reasons">
      <h2 id="stats-reasons" className={styles.cardTitle}>
        {t.reasonsTitle}
      </h2>
      <p className={styles.sub}>{t.reasonsSub(total)}</p>
      <ul className={styles.reasons}>
        {REASONS.map((reason, i) => (
          <li key={reason}>
            <span className={styles.reasonLabel}>{nl.companies.reasons[reason]}</span>
            <span className={styles.reasonTrack} aria-hidden="true">
              <span style={{ width: `${((counts[i] ?? 0) / max) * 100}%` }} />
            </span>
            <span className={styles.legendCount}>{counts[i] ?? 0}</span>
            <span className={styles.muted}>{pcts[i] ?? 0}%</span>
          </li>
        ))}
      </ul>
      {stats.msp_added > 0 && <p className={styles.sub}>→ {t.msp(stats.msp_added)}</p>}
    </section>
  );
}

function PlacesTable({ stats }: { stats: Stats }) {
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "companies", dir: "desc" });
  const rows = [...stats.municipalities].sort((a, b) => {
    if (sort.key === "name") return sort.dir === "asc" ? a.name.localeCompare(b.name, "nl") : b.name.localeCompare(a.name, "nl");
    const x = a[sort.key];
    const y = b[sort.key];
    // Without a value always last; ties by name.
    if (x === y || (x === null && y === null)) return a.name.localeCompare(b.name, "nl");
    if (x === null) return 1;
    if (y === null) return -1;
    return sort.dir === "asc" ? x - y : y - x;
  });
  const keys = Object.keys(t.columns) as SortKey[];
  return (
    <section className={styles.card} aria-labelledby="stats-places">
      <h2 id="stats-places" className={styles.cardTitle}>
        {t.placesTitle}
      </h2>
      <p className={styles.sub}>{t.placesSub}</p>
      <div className={styles.tableBox}>
        <table className={styles.table}>
          <thead>
            <tr>
              {keys.map((key) => (
                <th
                  key={key}
                  scope="col"
                  className={key === "name" ? undefined : styles.num}
                  aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button
                    type="button"
                    className={styles.sortButton}
                    onClick={() => {
                      setSort((current) =>
                        current.key === key
                          ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
                          : { key, dir: key === "name" || key === "response_days" ? "asc" : "desc" },
                      );
                    }}
                  >
                    {t.columns[key]}
                    <span aria-hidden="true" className={styles.sortMark}>
                      {sort.key === key ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name}>
                <th scope="row">{row.name}</th>
                <td className={styles.num}>{row.companies}</td>
                <td className={styles.num}>{row.kans}</td>
                <td className={styles.num}>{row.nee}</td>
                <td className={styles.num}>{row.response_days === null ? t.noResponse : t.days(row.response_days)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.sub}>{t.responseHelp}</p>
    </section>
  );
}

function Gaps({ stats }: { stats: Stats }) {
  return (
    <section className={styles.card} aria-labelledby="stats-gaps">
      <h2 id="stats-gaps" className={styles.cardTitle}>
        {t.gapsTitle}
      </h2>
      <p className={styles.sub}>{t.gapsLead}</p>
      {stats.gaps.length === 0 ? (
        <p className={styles.muted}>{t.gapsEmpty}</p>
      ) : (
        <ul className={styles.gaps}>
          {stats.gaps.map((gap) => (
            <li key={gap.name}>
              <span className={styles.gapName}>{gap.name}</span>
              <span className={styles.muted}>{t.gapCompanies(gap.companies)}</span>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Link className={buttonClass("secondary")} to="/zoeken">
          {t.addTip}
        </Link>
      </div>
    </section>
  );
}

export function StatsPage() {
  const [period, setPeriod] = useState<StatsPeriod>("semester");
  const stats = useQuery({ queryKey: ["admin", "stats", period], queryFn: () => getStats(period) });

  let body;
  if (stats.isPending) {
    body = (
      <div className={styles.skeletons} aria-hidden="true">
        <Skeleton height={110} />
        <Skeleton height={220} />
        <Skeleton height={220} />
      </div>
    );
  } else if (stats.isError) {
    body = (
      <EmptyState title={t.loadError} action={<Button onClick={() => void stats.refetch()}>{nl.common.retry}</Button>} />
    );
  } else {
    const data = stats.data;
    body = (
      <>
        <p className={styles.highlight}>
          <strong>{t.active(data.students_active, data.students_total)}</strong> {t.activeText(t.lower[data.period])}{" "}
          <span className={styles.muted}>{t.range(dayLabel(data.start), dayLabel(data.end))}</span>
        </p>
        <Kpis stats={data} />
        {data.reports === 0 && <EmptyState title={t.empty} />}
        <div className={styles.grid}>
          <StatusBlock stats={data} />
          <WeekChart stats={data} />
          <Reasons stats={data} />
          <Gaps stats={data} />
        </div>
        <PlacesTable stats={data} />
        <p className={styles.muted}>{t.footnote}</p>
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{nl.screens.stats}</h1>
        <a className={buttonClass("secondary")} href={exportUrl(period)} download>
          {t.export}
        </a>
      </div>
      <p className={styles.lead}>{t.lead}</p>
      <div className={styles.periods} role="group" aria-label={t.periodsLabel}>
        {PERIODS.map((option) => (
          <Chip
            key={option}
            active={period === option}
            onClick={() => {
              setPeriod(option);
            }}
          >
            {t.periods[option]}
          </Chip>
        ))}
      </div>
      <p className={styles.muted}>{t.exportHelp}</p>
      {body}
    </div>
  );
}
